import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { AppConfig } from 'src/app/app.config';
import { HelpersService } from 'src/app/services/helpers.service';
import { Statuses } from 'src/app/enums/statuses.enum'
import { TranslateService } from '@ngx-translate/core';
import { JobcardService } from 'src/app/services/jobcard.service';
import { ServiceRequestService } from 'src/app/services';
import { ConfirmationService, MessageService } from 'primeng/api';
import { WorkshopJobcardInternalExternalNotesComponent } from '../workshop-jobcard-live-updates/workshop-jobcard-internal-external-notes/workshop-jobcard-internal-external-notes.component';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ChecklistService } from 'src/app/services/checklist.service';
import { NotificationService } from 'src/app/services/notification.service';
import { Subscription } from 'rxjs';
import { BroadcastService } from 'src/app/services/broadcast.service';


@Component({
  selector: 'app-workshop-jobcard-live-notes',
  templateUrl: './workshop-jobcard-live-notes.component.html',
  styleUrls: ['./workshop-jobcard-live-notes.component.scss']
})
export class WorkshopJobcardLiveNotesComponent {

  @Input() jobcard: any;
  @Output() jobcardChange = new EventEmitter<any>();

  internalNotes: any;
  externalNotes: any;
  clientNotes: any;
  countryData: any = this.helpersService.getCountryData();
  loadingNotes: boolean = false;

  // Properties for inline note editing
  editingNote: any = null;
  updatingNote: boolean = false;
  actioningNote: boolean = false;
  isLocked: boolean = false;
  public subscription: Subscription;

  // Structured note configuration
  noteTypes = [
    {
      type: 'INTERNAL',
      titleKey: 'bikeshop.modal.update_job_card.job_card.internal_notes',
      showAll: false,
      newNote: { note: '', noteType: 'INTERNAL', isEditing: false },
      notes: null,
      inputRef: 'internalNoteInput'
    },
    {
      type: 'EXTERNAL',
      titleKey: 'bikeshop.modal.update_job_card.job_card.external_notes',
      showAll: false,
      newNote: { note: '', noteType: 'EXTERNAL', isEditing: false },
      notes: null,
      inputRef: 'externalNoteInput'
    },
    {
      type: 'CLIENTNOTES',
      titleKey: 'bikeshop.modal.update_job_card.job_card.customer_notes',
      showAll: false,
      newNote: { note: '', noteType: 'CLIENTNOTES', isEditing: false },
      notes: null,
      inputRef: 'clientNoteInput'
    }
  ]

  @ViewChild('internalNoteInput') internalNoteInput: ElementRef;
  @ViewChild('externalNoteInput') externalNoteInput: ElementRef;
  @ViewChild('clientNoteInput') clientNoteInput: ElementRef;

  constructor(
    private appConfig: AppConfig,
    private helpersService: HelpersService,
    private translate: TranslateService,
    public jobcardService: JobcardService,
    private serviceRequest: ServiceRequestService,
    private messageService: MessageService,
    private modalService: NgbModal,
    public activeModal: NgbActiveModal,
    private elementRef: ElementRef,
    private confirmationService: ConfirmationService,
    private checklistService: ChecklistService,
    private notificationService: NotificationService,
    public broadcastService: BroadcastService
  ) {

  }

  ngOnInit() {
    this.getJobCardNotes(true);
    this.createSubscription();
    this.isLocked = this.helpersService.isJobcardLocked(this.jobcard);
  }

  ngOnChanges() {
    this.isLocked = this.helpersService.isJobcardLocked(this.jobcard);
  }

  createSubscription() {
    this.subscription = this.broadcastService.getMessage().subscribe((data) => {
      let message = data.message;
      if (message?.message === 'refresh-jobcard-notes') {
        this.getJobCardNotes(false);
      }
    });
  }

  getJobCardNotes(initialLoad: boolean = false) {
    this.loadingNotes = initialLoad;
    this.jobcardService.getServiceNotes(this.jobcard.partnerID, this.jobcard.id).subscribe(
      (notes) => {
        // Update the noteTypes array with actual data
        this.noteTypes.forEach(noteType => {
          const foundNotes = notes.filter(note => note.noteType === noteType.type).pop();
          noteType.notes = foundNotes || {
            serviceID: this.jobcard.id,
            noteType: noteType.type,
            notes: []
          };
        });

        // Keep backward compatibility
        this.internalNotes = this.noteTypes.find(n => n.type === 'INTERNAL')?.notes;
        this.externalNotes = this.noteTypes.find(n => n.type === 'EXTERNAL')?.notes;
        this.clientNotes = this.noteTypes.find(n => n.type === 'CLIENTNOTES')?.notes;

        this.loadingNotes = false;
      },
      (error) => {
        this.loadingNotes = false;
      }
    );
  }

  getLatestNotes(notes: any[], limit: number = 2): any[] {
    if (!notes || notes.length === 0) {
      return [];
    }

    // Sort notes by created date (most recent first) and return limited number
    return notes
      .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
      .slice(0, limit);
  }

  // Button click handlers that check for long press
  onNotesButtonClick(noteType: string, event: Event) {
    this.openNotes(noteType);
  }

  openNotes(type) {
    const modalRef = this.modalService.open(WorkshopJobcardInternalExternalNotesComponent, {
      backdrop: true,
      backdropClass: 'jobcard-modal-popup-backdrop',
      windowClass: 'jobcard-modal-popup-window',
      size: 'xxl'
    });

    modalRef.componentInstance.jobcard = this.jobcard;
    modalRef.componentInstance.selectedNoteType = type;
    modalRef.componentInstance.internalNotes = this.internalNotes;
    modalRef.componentInstance.externalNotes = this.externalNotes;
    modalRef.componentInstance.clientNotes = this.clientNotes;

    modalRef.result.then(
      (result) => {
        this.jobcardChange.emit({ ...this.jobcard });
        this.getJobCardNotes();
      },
      (onrejected) => {
        this.getJobCardNotes();
      },
    );
  }

  // Inline note management methods
  startAddingNote(noteType: string) {
    //if (this.isLocked) return;
    const noteConfig = this.noteTypes.find(n => n.type === noteType);
    if (!noteConfig) return;

    noteConfig.newNote.isEditing = true;
    noteConfig.newNote.note = '';

    // Focus the appropriate input after view update using ID selector
    setTimeout(() => {
      const inputElement = document.getElementById(noteConfig.inputRef) as HTMLTextAreaElement;
      if (inputElement) {
        inputElement.focus();
      }
    }, 100);
  }

  /**
   * Handle keyboard shortcuts for note editing
   * Ctrl+Enter: Save note
   * Ctrl+Q: Cancel editing
   */
  onNoteKeyDown(event: KeyboardEvent, context: 'new' | 'edit', noteType?: string) {
    if (event.ctrlKey) {
      switch (event.key) {
        case 'Enter':
          event.preventDefault();
          if (context === 'new' && noteType) {
            this.saveNewNote(noteType);
          } else if (context === 'edit') {
            this.saveEditedNote();
          }
          break;
        case 'q':
        case 'Q':
          event.preventDefault();
          if (context === 'new' && noteType) {
            this.cancelAddingNote(noteType);
          } else if (context === 'edit') {
            this.cancelEditingNote();
          }
          break;
      }
    }
  }

  cancelAddingNote(noteType: string) {
    this.actioningNote = true;
    const noteConfig = this.noteTypes.find(n => n.type === noteType);
    if (!noteConfig) return;

    noteConfig.newNote.isEditing = false;
    noteConfig.newNote.note = '';
    this.clearActioningNote();
  }

  saveNewNoteBlur(noteType: string) {
    setTimeout(() => {
      if (!this.actioningNote) {
        this.saveNewNote(noteType);
      }
    }, 100)
  }

  clearActioningNote() {
    setTimeout(() => {
      this.actioningNote = false;
    }, 500);
  }

  saveNewNote(noteType: string) {
    this.actioningNote = true;
    const noteConfig = this.noteTypes.find(n => n.type === noteType);
    if (!noteConfig) return;

    const noteContent = noteConfig.newNote.note.trim();
    if (!noteContent) return;

    const newNote = {
      note: noteContent,
      noteType: noteType,
      serviceID: this.jobcard.id,
      partnerID: this.jobcard.partnerID,
    };

    this.updatingNote = true;
    this.jobcardService.addUpdateServiceNotes(newNote).subscribe(
      (result) => {
        this.getJobCardNotes();
        this.cancelAddingNote(noteType);
        this.updatingNote = false;
        this.jobcardChange.emit({ ...this.jobcard });
        this.clearActioningNote();
      },
      (error) => {
        this.updatingNote = false;
        this.clearActioningNote();
      }
    );
  }

  startEditingNote(note: any) {
    if (this.isLocked) return;
    this.editingNote = { ...note, originalNote: note.note };
  }

  cancelEditingNote() {
    this.actioningNote = true;
    this.editingNote = null;
    this.clearActioningNote();
  }

  saveEditedNoteBlur() {
    setTimeout(() => {
      if (!this.actioningNote) {
        this.saveEditedNote();
      }
    }, 100);
  }

  saveEditedNote() {
    this.actioningNote = true;
    if (!this.editingNote || !this.editingNote.note.trim()) return;

    const update = {
      id: this.editingNote.id,
      note: this.editingNote.note.trim(),
      noteType: this.editingNote.noteType,
      serviceID: this.jobcard.id,
      partnerID: this.jobcard.partnerID,
    };

    this.updatingNote = true;
    this.jobcardService.addUpdateServiceNotes(update).subscribe(
      (result) => {
        this.getJobCardNotes();
        this.cancelEditingNote();
        this.updatingNote = false;
        this.jobcardChange.emit({ ...this.jobcard });
        this.clearActioningNote();
      },
      (error) => {
        this.updatingNote = false;
        this.clearActioningNote();
      }
    );
  }

  deleteNote(note: any) {
    if (this.isLocked) return;
    this.actioningNote
    this.confirmationService.confirm({
      header: this.translate.instant('bikeshop.workshop.jobcard.manage.notes.confirm_delete'),
      message: this.translate.instant('bikeshop.workshop.jobcard.manage.notes.confirm_delete_message'),
      acceptLabel: this.translate.instant('general.yes'),
      rejectLabel: this.translate.instant('general.cancel'),
      accept: () => {

        this.updatingNote = true;
        this.jobcardService.deleteServiceNote(this.jobcard.partnerID, this.jobcard.id, note.id).subscribe(
          (result) => {
            this.getJobCardNotes();
            if (this.editingNote?.id === note.id) {
              this.cancelEditingNote();
            }
            this.updatingNote = false;
            this.jobcardChange.emit({ ...this.jobcard });
            this.clearActioningNote();
          },
          (error) => {
            this.updatingNote = false;
            this.clearActioningNote();
          }
        );
      }
    });
  }

  isEditing(note: any): boolean {
    return this.editingNote?.id === note.id;
  }

  hasNoteChanged(): boolean {
    return this.editingNote && this.editingNote.note !== this.editingNote.originalNote;
  }

  // Helper methods for template
  getNoteConfig(type: string) {
    return this.noteTypes.find(n => n.type === type);
  }

  toggleShowAll(type: string) {
    const noteConfig = this.noteTypes.find(n => n.type === type);
    if (noteConfig) {
      noteConfig.showAll = !noteConfig.showAll;
    }
  }

  shouldShowViewAll(type: string): boolean {
    const noteConfig = this.noteTypes.find(n => n.type === type);
    return noteConfig && noteConfig.notes?.notes?.length > 2 && !noteConfig.showAll;
  }

  shouldShowShowLess(type: string): boolean {
    const noteConfig = this.noteTypes.find(n => n.type === type);
    return noteConfig && noteConfig.notes?.notes?.length > 2 && noteConfig.showAll;
  }

}
